import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { useListTeamsQuery } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useOnboardingFinished } from '@hooks/useOnboardingActive';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { logError } from '@src/utils/logNonFatalError';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

export enum OwnerNudgeStep {
  CreateProject = 'create_project',
  InviteMember = 'invite_member',
  Chat = 'chat',
  CreateTask = 'create_task',
  AssignTask = 'assign_task',
}

export const OWNER_NUDGE_STEPS_ORDERED: OwnerNudgeStep[] = [
  OwnerNudgeStep.CreateProject,
  OwnerNudgeStep.InviteMember,
  OwnerNudgeStep.Chat,
  OwnerNudgeStep.CreateTask,
  OwnerNudgeStep.AssignTask,
];

const STORAGE_KEY_TEAM_ID = 'ownerNudgeTeamId';
const STORAGE_KEY_COMPLETED = 'ownerNudgeCompletedSteps';
const STORAGE_KEY_DISMISSED = 'ownerNudgeDismissed';

// Per-step retry storage for CreateProject nudge (1A)
const STORAGE_KEY_CP_DISMISS_COUNT = 'ownerNudgeCreateProjectDismissCount';
const STORAGE_KEY_CP_LAST_DISMISS_AT = 'ownerNudgeCreateProjectLastDismissAt';

// Retry delays after each "Later" on the CreateProject nudge: 24h, 48h
const CREATE_PROJECT_RETRY_DELAYS_MS = [
  24 * 60 * 60 * 1000,
  48 * 60 * 60 * 1000,
];
const CREATE_PROJECT_MAX_DISMISSES = 2;

const isNative = Platform.OS !== 'web';

type OwnerNudgeContextType = {
  isActive: boolean;
  teamId: string | null;
  completedSteps: OwnerNudgeStep[];
  isDismissed: boolean;
  currentStep: OwnerNudgeStep | null;
  isComplete: boolean;
  // CreateProject step retry visibility — use this to gate the banner
  isCreateProjectNudgeVisible: boolean;
  activateNudge: (newTeamId: string) => Promise<void>;
  completeStep: (step: OwnerNudgeStep) => Promise<void>;
  dismissNudge: () => Promise<void>;
  dismissCreateProjectStep: () => Promise<void>;
  markProjectCreated: () => Promise<void>;
};

const OwnerNudgeContext = createContext<OwnerNudgeContextType | undefined>(
  undefined
);

export const OwnerNudgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { trackEvent } = useMixpanel();
  const { me } = useMe();
  // TA-10920 #19: Smart Nudges only surface once onboarding has finished.
  const onboardingFinished = useOnboardingFinished();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<OwnerNudgeStep[]>([]);
  const [isDismissed, setIsDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // CreateProject per-step retry state
  const [cpDismissCount, setCpDismissCount] = useState(0);
  const [cpLastDismissAt, setCpLastDismissAt] = useState<number | null>(null);
  const [cpNow, setCpNow] = useState(() => Date.now());

  // Load persisted nudge state from AsyncStorage on mount.
  useEffect(() => {
    if (!isNative) {
      setLoaded(true);
      return;
    }
    const loadState = async () => {
      const [
        storedTeamId,
        storedCompleted,
        storedDismissed,
        storedCpCount,
        storedCpLastAt,
      ] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEY_TEAM_ID),
        AsyncStorage.getItem(STORAGE_KEY_COMPLETED),
        AsyncStorage.getItem(STORAGE_KEY_DISMISSED),
        AsyncStorage.getItem(STORAGE_KEY_CP_DISMISS_COUNT),
        AsyncStorage.getItem(STORAGE_KEY_CP_LAST_DISMISS_AT),
      ]);
      if (storedTeamId) setTeamId(storedTeamId);
      if (storedCompleted) {
        try {
          setCompletedSteps(JSON.parse(storedCompleted));
        } catch {
          // corrupt storage — start fresh
        }
      }
      if (storedDismissed === 'true') setIsDismissed(true);
      if (storedCpCount) setCpDismissCount(parseInt(storedCpCount, 10) || 0);
      if (storedCpLastAt)
        setCpLastDismissAt(parseInt(storedCpLastAt, 10) || null);
      setLoaded(true);
    };
    loadState();
  }, []);

  // Schedule a re-render exactly when the CreateProject retry window opens
  useEffect(() => {
    if (
      cpDismissCount === 0 ||
      cpDismissCount > CREATE_PROJECT_MAX_DISMISSES ||
      !cpLastDismissAt
    )
      return;
    const delay = CREATE_PROJECT_RETRY_DELAYS_MS[cpDismissCount - 1];
    const msUntilShow = cpLastDismissAt + delay - Date.now();
    if (msUntilShow <= 0) {
      setCpNow(Date.now());
      return;
    }
    const t = setTimeout(() => setCpNow(Date.now()), msUntilShow + 100);
    return () => clearTimeout(t);
  }, [cpDismissCount, cpLastDismissAt]);

  const activateNudge = useCallback(async (newTeamId: string) => {
    if (!isNative) return;
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_TEAM_ID, newTeamId),
      AsyncStorage.deleteItem(STORAGE_KEY_COMPLETED),
      AsyncStorage.deleteItem(STORAGE_KEY_DISMISSED),
      AsyncStorage.deleteItem(STORAGE_KEY_CP_DISMISS_COUNT),
      AsyncStorage.deleteItem(STORAGE_KEY_CP_LAST_DISMISS_AT),
    ]);
    setTeamId(newTeamId);
    setCompletedSteps([]);
    setIsDismissed(false);
    setCpDismissCount(0);
    setCpLastDismissAt(null);
    setCpNow(Date.now());
  }, []);

  // Auto-detect: query teams to find the most recently created team this user
  // owns. Runs on every app open so a team created on web is picked up.
  // Also used (when teamId known) to verify whether a project already exists,
  // so the CreateProject step auto-completes on next app open.
  const skipAutoDetect = !isNative || !loaded || !me?.id;

  // Skip project-existence check only when not on CreateProject step or
  // teamId unknown — reuses listTeams (Apollo serves from cache when fresh).
  const skipProjectCheck =
    !isNative ||
    !loaded ||
    !teamId ||
    isDismissed ||
    completedSteps.includes(OwnerNudgeStep.CreateProject);

  // Keep cache-and-network: this query must pick up teams created on other
  // devices/web between sessions (persisted Apollo cache would otherwise serve
  // stale data on app open). The nudge is a one-time flow so the extra request
  // is acceptable; TA-9877 direct-writes will not cover cross-device freshness.
  const { data: teamsData, refetch: refetchTeams } = useListTeamsQuery({
    variables: { includePending: false },
    fetchPolicy: 'cache-and-network',
    skip: skipAutoDetect && skipProjectCheck,
  });

  useEffect(() => {
    if (skipAutoDetect || !teamsData?.listTeams) return;

    const ownedTeams = teamsData.listTeams
      .filter((t) => !t.personal && t.owner?.id === me?.id)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

    if (ownedTeams.length > 0 && ownedTeams[0].uuid !== teamId) {
      activateNudge(ownedTeams[0].uuid);
    }
  }, [skipAutoDetect, teamsData, me?.id, teamId, activateNudge]);

  const completeStep = useCallback(
    async (step: OwnerNudgeStep) => {
      if (!isNative) return;
      if (!teamId || isDismissed) return;
      if (completedSteps.includes(step)) return;

      const stepIndex = OWNER_NUDGE_STEPS_ORDERED.indexOf(step);
      // enforce sequential order
      if (
        stepIndex > 0 &&
        !completedSteps.includes(OWNER_NUDGE_STEPS_ORDERED[stepIndex - 1])
      ) {
        return;
      }

      const next = [...completedSteps, step];
      setCompletedSteps(next);
      await AsyncStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(next));

      trackEvent(MIXPANEL_EVENTS.OWNER_NUDGE_STEP_COMPLETED, {
        step,
        completed_count: next.length,
        team_id: teamId,
      });

      if (next.length === OWNER_NUDGE_STEPS_ORDERED.length) {
        trackEvent(MIXPANEL_EVENTS.OWNER_NUDGE_COMPLETED, { team_id: teamId });
      }
    },
    [teamId, isDismissed, completedSteps, trackEvent]
  );

  // Auto-complete CreateProject step if the team already has projects.
  // Handles app open after project was created (so nudge doesn't reappear).
  useEffect(() => {
    if (skipProjectCheck || !teamsData?.listTeams || !teamId) return;

    const nudgeTeam = teamsData.listTeams.find((t) => t.uuid === teamId);
    if (nudgeTeam?.hasProjects) {
      completeStep(OwnerNudgeStep.CreateProject);
    }
  }, [skipProjectCheck, teamsData, teamId, completeStep]);

  // Called after a team project is created. completeStep alone is not enough:
  // during onboarding the nudge team may not be activated yet (listTeams was
  // fetched before the team/project existed), so completeStep bails out and
  // the stale `hasProjects: false` keeps the banner visible. Refetching lets
  // auto-detect + the hasProjects effect settle on fresh data.
  const markProjectCreated = useCallback(async () => {
    if (!isNative) return;
    await completeStep(OwnerNudgeStep.CreateProject);
    try {
      await refetchTeams();
    } catch (error) {
      logError({
        message: 'ownerNudge refetchTeams failed',
        errorMessage: (error as Error)?.message,
        operationName: 'listTeams',
      });
    }
  }, [completeStep, refetchTeams]);

  const dismissNudge = useCallback(async () => {
    if (!isNative) return;
    setIsDismissed(true);
    await AsyncStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
    trackEvent(MIXPANEL_EVENTS.OWNER_NUDGE_DISMISSED, { team_id: teamId });
  }, [teamId, trackEvent]);

  // Defers the CreateProject nudge with retry-at-24h/48h — does NOT
  // permanently dismiss the whole flow.
  const dismissCreateProjectStep = useCallback(async () => {
    if (!isNative) return;
    const next = cpDismissCount + 1;
    const ts = Date.now();
    setCpDismissCount(next);
    setCpLastDismissAt(ts);
    setCpNow(ts);
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEY_CP_DISMISS_COUNT, String(next)),
      AsyncStorage.setItem(STORAGE_KEY_CP_LAST_DISMISS_AT, String(ts)),
    ]);
    trackEvent(MIXPANEL_EVENTS.OWNER_NUDGE_DISMISSED, {
      team_id: teamId,
      step: OwnerNudgeStep.CreateProject,
      dismiss_count: next,
    });
  }, [cpDismissCount, teamId, trackEvent]);

  const currentStep = useMemo(() => {
    if (!loaded || !teamId || isDismissed) return null;
    return (
      OWNER_NUDGE_STEPS_ORDERED.find((s) => !completedSteps.includes(s)) ?? null
    );
  }, [loaded, teamId, isDismissed, completedSteps]);

  // Banner on Chats only shows step 1A (CreateProject) with retry timing.
  // Other steps live in their own dedicated components/screens.
  const isCreateProjectNudgeVisible = useMemo(() => {
    if (!onboardingFinished) return false;
    if (currentStep !== OwnerNudgeStep.CreateProject) return false;
    if (!loaded || !teamId) return false;
    if (cpDismissCount === 0) return true;
    if (cpDismissCount > CREATE_PROJECT_MAX_DISMISSES) return false;
    if (!cpLastDismissAt) return true;
    const delay = CREATE_PROJECT_RETRY_DELAYS_MS[cpDismissCount - 1];
    return cpNow - cpLastDismissAt >= delay;
  }, [
    onboardingFinished,
    currentStep,
    loaded,
    teamId,
    cpDismissCount,
    cpLastDismissAt,
    cpNow,
  ]);

  const isActive = loaded && !!teamId && !isDismissed;
  const isComplete = completedSteps.length === OWNER_NUDGE_STEPS_ORDERED.length;

  const value = useMemo<OwnerNudgeContextType>(
    () => ({
      isActive,
      teamId,
      completedSteps,
      isDismissed,
      currentStep,
      isComplete,
      isCreateProjectNudgeVisible,
      activateNudge,
      completeStep,
      dismissNudge,
      dismissCreateProjectStep,
      markProjectCreated,
    }),
    [
      isActive,
      teamId,
      completedSteps,
      isDismissed,
      currentStep,
      isComplete,
      isCreateProjectNudgeVisible,
      activateNudge,
      completeStep,
      dismissNudge,
      dismissCreateProjectStep,
      markProjectCreated,
    ]
  );

  return (
    <OwnerNudgeContext.Provider value={value}>
      {children}
    </OwnerNudgeContext.Provider>
  );
};

export function useOwnerNudge(): OwnerNudgeContextType {
  const ctx = useContext(OwnerNudgeContext);
  if (!ctx) {
    throw new Error('useOwnerNudge must be used within OwnerNudgeProvider');
  }
  return ctx;
}
